import { FC, useEffect, useRef, useState } from "react";
import { FaRegHeart, FaHeart, FaRegTrashCan } from "react-icons/fa6";
import { Link } from "react-router-dom";


import styles from "./styles.module.css";
import defaultProductImg from "src/assets/images/default_product_img.png";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { QtyInput } from "src/components";
import { useGuestWishlistState, useWishlist } from "src/hooks";
import { authSelector } from "src/store/reducers/auth";
import { isRTL, truncateWords } from "src/utils/global";
import { ProductItemType } from "src/types/products/ProductItem";
import useCartAction from "src/hooks/useCartAction";


interface ProductProp {
    product: ProductItemType;
    variant?: "home" | "wishlist";
}

const Product: FC<ProductProp> = ({ product, variant }) => {
    const { t } = useTranslation("products");
    const { qty, cartAddPress, cartRemovePress } = useCartAction({
        slug: product.slug,
        name: product.name,
        image: product.thumbnail,
        qty: 1,
        price: product.price,
        disc_price: product.discounted_price,
        stock: product.stock || null
    });
    const auth = useSelector(authSelector);
    const { slugs: guestWishlistSlugs, syncState: guestWishlistSyncState } = useGuestWishlistState();
    const [inWishlist, setInWishlist] = useState(product.in_wishlist);
    const [isWishlistPending, setIsWishlistPending] = useState(false);
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const [imageSrc, setImageSrc] = useState(product.thumbnail);
    const deleteButtonRef = useRef<HTMLButtonElement>(null);
    const { wishlistAdding, wishlistRemoving } = useWishlist();

    const isDisabled = !(product.stock > 0 || product.available);
    const hasDiscount = product.discounted_price !== null && product.discounted_price < product.price;
    const isWishlistVariant = variant === "wishlist";
    const isGuestWishlisted = guestWishlistSlugs.includes(product.slug);

    const wishlistToggleHandle = async () => {
        if (isWishlistPending) {
            return;
        }

        const previousValue = inWishlist;
        const nextValue = !previousValue;
        setIsWishlistPending(true);
        setInWishlist(nextValue);

        try {
            const success = nextValue
                ? await wishlistAdding(product)
                : await wishlistRemoving(product);

            if (!success) {
                setInWishlist(previousValue);
            }
        } catch (_error) {
            setInWishlist(previousValue);
        } finally {
            setIsWishlistPending(false);
        }
    }

    const wishlistDeletePress = async () => {
        if (isWishlistPending) {
            return;
        }

        if (!isConfirmingDelete) {
            setIsConfirmingDelete(true);
            return;
        }

        setIsConfirmingDelete(false);
        setIsWishlistPending(true);

        try {
            const success = await wishlistRemoving(product);

            if (success) {
                setInWishlist(false);
            }
        } catch (_error) {
            // useWishlist normally reports failures and returns false. Keep the
            // card unchanged if an unexpected rejection still reaches here.
        } finally {
            setIsWishlistPending(false);
        }
    }

    useEffect(() => {
        setImageSrc(product.thumbnail);
    }, [product.thumbnail]);

    useEffect(() => {
        if (!isConfirmingDelete) {
            return;
        }

        const cancel = (event: MouseEvent) => {
            if (deleteButtonRef.current?.contains(event.target as Node)) {
                return;
            }

            setIsConfirmingDelete(false);
        };
        const cancelOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsConfirmingDelete(false);
            }
        };

        document.addEventListener("mousedown", cancel);
        document.addEventListener("keydown", cancelOnEscape);

        return () => {
            document.removeEventListener("mousedown", cancel);
            document.removeEventListener("keydown", cancelOnEscape);
        };
    }, [isConfirmingDelete]);

    useEffect(() => {
        if (!auth.access) {
            setInWishlist(isGuestWishlisted);
            return;
        }

        if (guestWishlistSyncState === "syncing" && isGuestWishlisted) {
            setInWishlist(true);
            return;
        }

        setInWishlist(product.in_wishlist);
    }, [auth.access, guestWishlistSyncState, isGuestWishlisted, product.in_wishlist]);


    return (
        <div className={`${styles.product} ${variant === "home" ? styles.home : ""} ${isWishlistVariant ? styles.wishlistPage : ""} ${isRTL() && styles.rtl}`}>
            <div className={styles.card}>
                <div className={styles.img__con}>
                    <img
                        src={imageSrc || defaultProductImg}
                        alt={product.name}
                        loading="lazy"
                        width={308}
                        height={231}
                        onError={() => setImageSrc((currentSrc) => (
                            currentSrc === defaultProductImg ? currentSrc : defaultProductImg
                        ))}
                    />

                    <div className={styles.img__header}>
                        {hasDiscount ? (
                            <span className={styles.discountBadge}>
                                {t("product.offer")}
                            </span>
                        ) : <span aria-hidden="true" />}
                        <button
                            type="button"
                            className={`${styles.wishlist} ${inWishlist ? styles.item__active : ''}`}
                            onClick={wishlistToggleHandle}
                            disabled={isWishlistPending}
                            aria-label={`${inWishlist ? t("wishlist.remove") : t("wishlist.add")} ${product.name}`}
                            aria-pressed={inWishlist}
                            aria-busy={isWishlistPending}
                        >
                            {!inWishlist ? <FaRegHeart size={16} /> : <FaHeart size={16} />}
                        </button>
                    </div>
                    <Link
                        to={`/products/${product.slug}/`}
                        className={styles.overlay}
                        aria-label={product.name}
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>
                <div className={styles.content}>
                    <h4 className={styles.title}>
                        <Link to={`/products/${product.slug}/`}>
                            {truncateWords(`${product.name}`, 6)}
                        </Link>
                    </h4>

                    {
                        product.stock > 1 ? <div className={styles.in_stock}>
                            {t("product.in_stock")}
                        </div> :
                            product.available ?
                                <div className={styles.available}>
                                    {t("product.available")}
                                </div>
                                :
                                <div className={styles.unavailable}>
                                    {t("product.unavailable")}
                                </div>
                    }
                    <p className={styles.price}>
                        {hasDiscount && <span className={styles.discounted_price}>
                            {product.discounted_price} {t('common:lyd')}
                        </span>}
                        <span className={`${styles.original} ${hasDiscount ? styles.org_discounted_price : ''}`}>
                            {product.price} {t('common:lyd')}
                        </span>
                    </p>
                    <div>
                        {
                            isDisabled ? <div className={styles.unavailable_btn}>
                                {t("product.unavailable")}
                            </div> :
                                (qty < 1 ?
                                    <button type="button" onClick={cartAddPress} className={`btn__primary ${styles.cart_add_btn}`}>
                                        {t('common:add_to_cart')}
                                    </button> :
                                    <div className={styles.remove__item}>
                                        <button type="button" onClick={cartRemovePress} id="variant_3" className={`btn__primary ${styles.cart_remove_btn}`} aria-label={t('cart.remove')}>
                                            <FaRegTrashCan className={styles.removeIcon} aria-hidden="true" />
                                            <span className={styles.removeLabel}>{t('cart.remove')}</span>
                                        </button>
                                        <div className={styles.in_Cart_qty}>
                                            <QtyInput qty={qty} itemSlug={product.slug} maxQty={product.stock > 0 ? product.stock : undefined} fluid />
                                        </div>
                                    </div>)
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}


export default Product;
